import { type Address, type PublicClient, type WalletClient, type Hex, type Hash, type Account } from 'viem';
import { MicroPaymentChannelABI } from '../abis/index.js';
import { validateAddress, validateRequired, validateDeployedAddress } from '../validators/index.js';
import { AAStarError } from '../errors/index.js';

export type ChannelState = {
    payer: Address;
    payee: Address;
    token: Address;
    authorizedSigner: Address;
    deposit: bigint;
    settled: bigint;
    closeRequestedAt: bigint;
    finalized: boolean;
};

export type ChannelActions = {
    // Write
    openChannel: (args: {
        payee: Address, token: Address, deposit: bigint, salt: Hex,
        authorizedSigner: Address, account?: Account | Address
    }) => Promise<Hash>;
    settleChannel: (args: {
        channelId: Hex, cumulativeAmount: bigint, signature: Hex,
        account?: Account | Address
    }) => Promise<Hash>;
    closeChannel: (args: {
        channelId: Hex, cumulativeAmount: bigint, signature: Hex,
        account?: Account | Address
    }) => Promise<Hash>;
    topUpChannel: (args: {
        channelId: Hex, amount: bigint, account?: Account | Address
    }) => Promise<Hash>;
    requestCloseChannel: (args: {
        channelId: Hex, account?: Account | Address
    }) => Promise<Hash>;
    withdrawChannel: (args: {
        channelId: Hex, account?: Account | Address
    }) => Promise<Hash>;

    // Read
    getChannel: (args: { channelId: Hex }) => Promise<ChannelState>;
    /**
     * @deprecated The deployed MicroPaymentChannel ABI no longer exposes a `CLOSE_TIMEOUT`
     * constant — it was replaced by the configurable {@link closeTimeout} getter plus the
     * {@link MIN_CLOSE_TIMEOUT}/{@link MAX_CLOSE_TIMEOUT} bounds. This wrapper now delegates
     * to the on-chain `closeTimeout()` getter so it no longer reverts; use {@link closeTimeout}.
     */
    CLOSE_TIMEOUT: () => Promise<bigint>;
    /** Current configurable close-timeout (seconds, view). ABI: closeTimeout() -> uint64. */
    closeTimeout: () => Promise<bigint>;
    /** Set the configurable close-timeout window (owner-gated). ABI: setCloseTimeout(uint64 _timeout). */
    setCloseTimeout: (args: { timeout: bigint, account?: Account | Address }) => Promise<Hash>;
    /** Whether a given channelId has already been closed (view). ABI: closedChannels(bytes32) -> bool. */
    closedChannels: (args: { channelId: Hex }) => Promise<boolean>;
    /** Upper bound for {@link setCloseTimeout} (view). ABI: MAX_CLOSE_TIMEOUT() -> uint64. */
    MAX_CLOSE_TIMEOUT: () => Promise<bigint>;
    /** Lower bound for {@link setCloseTimeout} (view). ABI: MIN_CLOSE_TIMEOUT() -> uint64. */
    MIN_CLOSE_TIMEOUT: () => Promise<bigint>;
    VOUCHER_TYPEHASH: () => Promise<Hex>;
    version: () => Promise<string>;
};

export const channelActions = (address: Address) => (client: PublicClient | WalletClient): ChannelActions => {
    validateDeployedAddress(address, 'MicroPaymentChannel');
    return ({
    // --- Write ---
    async openChannel({ payee, token, deposit, salt, authorizedSigner, account }) {
        try {
            validateAddress(payee, 'payee');
            validateAddress(token, 'token');
            validateRequired(salt, 'salt');
            validateAddress(authorizedSigner, 'authorizedSigner');
            return await (client as any).writeContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'openChannel',
                args: [payee, token, deposit, salt, authorizedSigner],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'openChannel');
        }
    },

    async settleChannel({ channelId, cumulativeAmount, signature, account }) {
        try {
            validateRequired(channelId, 'channelId');
            validateRequired(signature, 'signature');
            return await (client as any).writeContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'settleChannel',
                args: [channelId, cumulativeAmount, signature],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'settleChannel');
        }
    },

    async closeChannel({ channelId, cumulativeAmount, signature, account }) {
        try {
            validateRequired(channelId, 'channelId');
            validateRequired(signature, 'signature');
            return await (client as any).writeContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'closeChannel',
                args: [channelId, cumulativeAmount, signature],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'closeChannel');
        }
    },

    async topUpChannel({ channelId, amount, account }) {
        try {
            validateRequired(channelId, 'channelId');
            return await (client as any).writeContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'topUpChannel',
                args: [channelId, amount],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'topUpChannel');
        }
    },

    async requestCloseChannel({ channelId, account }) {
        try {
            validateRequired(channelId, 'channelId');
            return await (client as any).writeContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'requestCloseChannel',
                args: [channelId],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'requestCloseChannel');
        }
    },

    async withdrawChannel({ channelId, account }) {
        try {
            validateRequired(channelId, 'channelId');
            return await (client as any).writeContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'withdrawChannel',
                args: [channelId],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'withdrawChannel');
        }
    },

    // --- Read ---
    async getChannel({ channelId }) {
        try {
            validateRequired(channelId, 'channelId');
            const result = await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'getChannel',
                args: [channelId]
            }) as any;
            return {
                payer: result.payer as Address,
                payee: result.payee as Address,
                token: result.token as Address,
                authorizedSigner: result.authorizedSigner as Address,
                deposit: result.deposit as bigint,
                settled: result.settled as bigint,
                closeRequestedAt: result.closeRequestedAt as bigint,
                finalized: result.finalized as boolean,
            } as ChannelState;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'getChannel');
        }
    },

    async CLOSE_TIMEOUT() {
        try {
            // On-chain fn: closeTimeout() — the legacy `CLOSE_TIMEOUT` constant was removed.
            return await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'closeTimeout'
            }) as bigint;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'CLOSE_TIMEOUT');
        }
    },

    async closeTimeout() {
        try {
            return await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'closeTimeout'
            }) as bigint;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'closeTimeout');
        }
    },

    async setCloseTimeout({ timeout, account }) {
        try {
            validateRequired(timeout, 'timeout');
            return await (client as any).writeContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'setCloseTimeout',
                args: [timeout],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'setCloseTimeout');
        }
    },

    async closedChannels({ channelId }) {
        try {
            validateRequired(channelId, 'channelId');
            return await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'closedChannels',
                args: [channelId]
            }) as boolean;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'closedChannels');
        }
    },

    async MAX_CLOSE_TIMEOUT() {
        try {
            return await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'MAX_CLOSE_TIMEOUT'
            }) as bigint;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'MAX_CLOSE_TIMEOUT');
        }
    },

    async MIN_CLOSE_TIMEOUT() {
        try {
            return await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'MIN_CLOSE_TIMEOUT'
            }) as bigint;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'MIN_CLOSE_TIMEOUT');
        }
    },

    async VOUCHER_TYPEHASH() {
        try {
            return await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'VOUCHER_TYPEHASH'
            }) as Hex;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'VOUCHER_TYPEHASH');
        }
    },

    async version() {
        try {
            return await (client as PublicClient).readContract({
                address, abi: MicroPaymentChannelABI,
                functionName: 'version'
            }) as string;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'version');
        }
    },
});
};
